import { BlurFilter, Container, Graphics, Sprite } from 'pixi.js';
import type { SpinTiming, SymbolId } from '../types';
import { Easings, type Easing, type Timeline } from './tween';
import type { TextureStore } from './textures';

type Phase = 'idle' | 'windup' | 'run' | 'land';

/** backOut parameter whose peak overshoot equals `fraction` of the travelled distance. */
function backParam(fraction: number): number {
  if (fraction <= 0) return 0;
  let lo = 0;
  let hi = 6;
  for (let i = 0; i < 40; i++) {
    const s = (lo + hi) / 2;
    const peak = (4 * s * s * s) / (27 * (s + 1) * (s + 1));
    if (peak < fraction) lo = s;
    else hi = s;
  }
  return (lo + hi) / 2;
}

/**
 * One reel column. `position` is a continuous strip index: strip[i] is drawn at
 * y = (i - position) * pitch, so spinning "down" means position decreasing.
 * Landing at an integer position s shows strip[s .. s+rows-1] top to bottom.
 */
export class ReelView extends Container {
  readonly index: number;
  private readonly strip: SymbolId[];
  private readonly rows: number;
  private symbolWidth: number;
  private symbolHeight: number;
  private gapY: number;
  private readonly textures: TextureStore;
  private readonly timeline: Timeline;
  private readonly blurStrength: number;

  private readonly body = new Container();
  private readonly maskShape = new Graphics();
  private readonly glow = new Graphics();
  private readonly sprites: Sprite[] = [];
  private readonly spriteSymbol: (SymbolId | null)[] = [];
  private readonly blur = new BlurFilter({ strengthX: 0, strengthY: 0, quality: 2 });

  /** Column overrides for grids that do not exist on the strip (setGrid / restore). Keyed by absolute index. */
  private overrides = new Map<number, SymbolId>();
  private pos = 0;
  private phase: Phase = 'idle';
  private elapsed = 0;
  private timing!: SpinTiming;
  private stopAtMs = 0;
  private p0 = 0;
  private target = 0;
  /** Strip index of the landing column (>= 0) or -1 when the column is not on the strip. */
  private targetStop = -1;
  private targetColumn: SymbolId[] = [];
  private runStart = 0;
  private landStart = 0;
  private landDur = 0;
  private landEase: Easing = Easings.quadOut;
  private stopLoop: (() => void) | null = null;
  private glowLoop: (() => void) | null = null;
  private resolveSpin: (() => void) | null = null;
  private onStop: (() => void) | null = null;
  private blurred = false;

  constructor(o: {
    index: number;
    strip: SymbolId[];
    rows: number;
    symbolWidth: number;
    symbolHeight: number;
    gapY: number;
    textures: TextureStore;
    timeline: Timeline;
    blurStrength: number;
  }) {
    super();
    this.index = o.index;
    this.strip = o.strip;
    this.rows = o.rows;
    this.symbolWidth = o.symbolWidth;
    this.symbolHeight = o.symbolHeight;
    this.gapY = o.gapY;
    this.textures = o.textures;
    this.timeline = o.timeline;
    this.blurStrength = o.blurStrength;

    this.addChild(this.glow, this.body, this.maskShape);
    this.body.mask = this.maskShape;
    for (let j = 0; j < this.rows + 2; j++) {
      const sp = new Sprite();
      sp.anchor.set(0.5);
      this.body.addChild(sp);
      this.sprites.push(sp);
      this.spriteSymbol.push(null);
    }
    this.glow.visible = false;
    this.resize(this.symbolWidth, this.symbolHeight, this.gapY);
  }

  // ------------------------------------------------------------------ geometry

  private get pitch(): number {
    return this.symbolHeight + this.gapY;
  }

  get windowHeight(): number {
    return this.rows * this.symbolHeight + (this.rows - 1) * this.gapY;
  }

  resize(symbolWidth: number, symbolHeight: number, gapY: number): void {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.gapY = gapY;
    this.maskShape.clear().rect(0, 0, symbolWidth, this.windowHeight).fill(0xffffff);
    for (const sp of this.sprites) {
      sp.x = symbolWidth / 2;
      sp.width = symbolWidth;
      sp.height = symbolHeight;
    }
    this.blur.strengthY = 0;
    this.render();
  }

  // ------------------------------------------------------------------ symbols

  private wrap(i: number): number {
    const n = this.strip.length;
    return ((i % n) + n) % n;
  }

  private symbolAt(i: number): SymbolId {
    return this.overrides.get(i) ?? this.strip[this.wrap(i)];
  }

  /** Strip index whose window equals `column`, or -1. */
  private findStop(column: SymbolId[]): number {
    const n = this.strip.length;
    const candidates: number[] = [];
    for (let s = 0; s < n; s++) {
      let ok = true;
      for (let r = 0; r < this.rows && ok; r++) if (this.strip[(s + r) % n] !== column[r]) ok = false;
      if (ok) candidates.push(s);
    }
    if (!candidates.length) return -1;
    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  /** Redraw sprites for the current position. */
  private render(): void {
    const base = Math.floor(this.pos);
    const useBlur = this.blurred;
    for (let j = 0; j < this.sprites.length; j++) {
      const i = base - 1 + j;
      const sp = this.sprites[j];
      const id = this.symbolAt(i);
      const key = useBlur ? `${id}#blur` : id;
      if (this.spriteSymbol[j] !== key) {
        const blurTex = useBlur ? this.textures.symbolBlur(id) : null;
        sp.texture = blurTex ?? this.textures.symbol(id);
        sp.width = this.symbolWidth;
        sp.height = this.symbolHeight;
        this.spriteSymbol[j] = key;
      }
      sp.y = (i - this.pos) * this.pitch + this.symbolHeight / 2;
    }
  }

  setColumn(column: SymbolId[]): void {
    this.finishSpin(false);
    const s = this.findStop(column);
    this.overrides.clear();
    if (s >= 0) this.pos = s;
    else {
      this.pos = Math.round(this.pos);
      column.forEach((id, r) => this.overrides.set(this.pos + r, id));
    }
    this.setBlurred(false);
    this.render();
  }

  get symbolSprites(): Sprite[] {
    // sprites[j] holds index floor(position) - 1 + j => row r is j = r + 1
    return this.sprites.slice(1, 1 + this.rows);
  }

  // ------------------------------------------------------------------ spinning

  spinTo(
    column: SymbolId[],
    o: { stopAtMs: number; timing: SpinTiming; quick: boolean; anticipation: boolean; glowColor: number; onStop: () => void },
  ): Promise<void> {
    this.finishSpin(false);
    this.timing = o.timing;
    this.stopAtMs = o.stopAtMs;
    this.onStop = o.onStop;
    this.elapsed = 0;
    this.phase = 'windup';
    this.p0 = this.pos;
    this.targetColumn = column;
    this.targetStop = this.findStop(column);
    if (o.anticipation) this.setGlow(o.glowColor);
    return new Promise<void>((resolve) => {
      this.resolveSpin = resolve;
      this.stopLoop = this.timeline.loop({ period: 1000, update: (_t, dt) => this.tick(dt) });
    });
  }

  /**
   * Absolute landing index: at least `minTravel` symbols below `from` (position decreasing).
   * Columns that are not on the strip get overrides written at the chosen index.
   */
  private computeTarget(from: number, minTravel: number): number {
    const n = this.strip.length;
    if (this.targetStop >= 0) {
      const k = Math.floor((from - minTravel - this.targetStop) / n);
      return this.targetStop + k * n;
    }
    const t = Math.floor(from - minTravel);
    this.targetColumn.forEach((id, r) => this.overrides.set(t + r, id));
    return t;
  }

  private setBlurred(on: boolean): void {
    if (this.blurred === on) return;
    this.blurred = on;
    this.body.filters = on && this.blurStrength > 0 ? [this.blur] : null;
    if (!on) this.blur.strengthY = 0;
    this.spriteSymbol.fill(null);
  }

  private tick(dt: number): void {
    if (this.phase === 'idle') return;
    this.elapsed += dt;
    const T = this.timing;
    if (this.phase === 'windup') {
      const t0 = T.pressMs;
      if (this.elapsed >= t0) {
        const p = Math.min(1, (this.elapsed - t0) / Math.max(1, T.windUpMs));
        this.pos = this.p0 + T.windUpSymbols * Easings.quadOut(p);
        if (p >= 1) {
          this.phase = 'run';
          this.runStart = this.elapsed;
          this.setBlurred(true);
        }
      }
    } else if (this.phase === 'run') {
      const tr = this.elapsed - this.runStart;
      const frac = Math.min(1, tr / Math.max(1, T.accelMs));
      const speed = T.speedSymbolsPerSec * frac;
      this.pos -= (speed * dt) / 1000;
      this.blur.strengthY = this.blurStrength * this.pitch * 0.12 * frac;
      if (this.elapsed >= this.stopAtMs - T.stopEaseMs) this.beginLanding(T.stopEaseMs, T.overshootSymbols, T.stopEasing);
    }
    if (this.phase === 'land') {
      const lt = Math.min(1, (this.elapsed - this.landStart) / Math.max(1, this.landDur));
      this.pos = this.p0 + (this.target - this.p0) * this.landEase(lt);
      if (lt >= 1) {
        this.pos = this.target;
        this.render();
        this.finishSpin(true);
        return;
      }
    }
    this.render();
  }

  private beginLanding(durationMs: number, overshootSymbols: number, easingName: SpinTiming['stopEasing']): void {
    this.phase = 'land';
    this.landStart = this.elapsed;
    this.landDur = durationMs;
    this.p0 = this.pos;
    const travel = Math.max(1.5, (this.timing.speedSymbolsPerSec * durationMs) / 1000 * 0.7);
    this.target = this.computeTarget(this.p0, travel);
    const dist = Math.max(0.001, this.p0 - this.target);
    if (easingName === 'backOut') this.landEase = Easings.backOutWith(backParam(overshootSymbols / dist));
    else this.landEase = Easings[easingName] ?? Easings.quadOut;
    this.setBlurred(false);
    this.blur.strengthY = 0;
  }

  /** Quick stop: land within the configured window with a soft ease and no big overshoot. */
  landNow(): void {
    if (this.phase === 'idle') return;
    if (this.phase === 'land') return;
    const dur = Math.max(80, this.landDurQuick());
    this.beginLanding(dur, 0.05, 'backOut');
  }

  private landDurQuick(): number {
    return 250;
  }

  private finishSpin(landed: boolean): void {
    if (this.phase === 'idle' && !this.stopLoop) return;
    this.phase = 'idle';
    this.stopLoop?.();
    this.stopLoop = null;
    this.setBlurred(false);
    this.setGlow(null);
    // Drop overrides that are no longer visible.
    if (this.overrides.size) {
      const base = Math.round(this.pos);
      for (const k of [...this.overrides.keys()]) if (k < base - 1 || k > base + this.rows) this.overrides.delete(k);
    }
    const resolve = this.resolveSpin;
    const onStop = this.onStop;
    this.resolveSpin = null;
    this.onStop = null;
    if (landed) onStop?.();
    resolve?.();
  }

  // ------------------------------------------------------------------ glow

  setGlow(color: number | null): void {
    this.glowLoop?.();
    this.glowLoop = null;
    if (color == null) {
      this.glow.visible = false;
      return;
    }
    const pad = 6;
    this.glow
      .clear()
      .roundRect(-pad, -pad, this.symbolWidth + pad * 2, this.windowHeight + pad * 2, 12)
      .fill({ color, alpha: 0.55 });
    this.glow.visible = true;
    this.glowLoop = this.timeline.loop({
      period: 600,
      update: (t) => {
        this.glow.alpha = 0.55 + 0.45 * Math.sin(t * Math.PI * 2);
      },
    });
  }

  override destroy(): void {
    this.finishSpin(false);
    this.body.filters = null;
    this.blur.destroy();
    super.destroy({ children: true });
  }
}
